import { AlertTriangle, FolderOpen, FolderSync, HardDrive } from 'lucide-react';
import { useState } from 'react';
import { tip } from '../../lib/tooltip.ts';
import { mediaService } from '../../media/setup.ts';
import { useMediaRuntime } from '../../media/service.ts';
import type { ProviderAccess } from '../../media/types.ts';
import { errorMessage, toast } from '../../state/toast-store.ts';

/**
 * Shows the connection state of the project's media storage and the one-click actions to fix it
 * (choose a folder, re-grant access after a reload, relocate a moved folder). Provider-agnostic:
 * it renders whatever access state each provider reports.
 */
export function MediaFolderBar() {
  const access = useMediaRuntime((s) => s.access);
  const runtime = useMediaRuntime((s) => s.runtime);
  const [busy, setBusy] = useState(false);
  const importId = mediaService.registry.importProvider.id;
  const offline = Object.values(runtime).filter(
    (r) => r.availability === 'offline' || r.availability === 'error',
  ).length;

  const connect = async (providerId: string, relocate = false) => {
    setBusy(true);
    try {
      const result = await mediaService.connect(providerId, { relocate });
      if (result.state === 'ready') {
        const stillOffline = Object.values(useMediaRuntime.getState().runtime).filter(
          (r) => r.availability === 'offline' || r.availability === 'error',
        ).length;
        if (stillOffline > 0) toast.warn(`${stillOffline} file(s) were not found in “${result.label}”`);
      }
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const entries = Object.entries(access).sort(([a], [b]) => (a === importId ? -1 : b === importId ? 1 : 0));
  return (
    <div className="media-folder">
      {entries.map(([providerId, state]) => (
        <ProviderRow
          key={providerId}
          state={state}
          isImport={providerId === importId}
          busy={busy}
          offline={providerId === importId ? offline : 0}
          onConnect={(relocate) => void connect(providerId, relocate)}
        />
      ))}
    </div>
  );
}

function ProviderRow({
  state,
  isImport,
  busy,
  offline,
  onConnect,
}: {
  state: ProviderAccess;
  isImport: boolean;
  busy: boolean;
  offline: number;
  onConnect(relocate: boolean): void;
}) {
  switch (state.state) {
    case 'not-configured':
      return (
        <div className="media-folder-callout">
          <FolderOpen size={18} />
          <div>
            <strong>Connect the project folder</strong>
            <p>This project’s folder is not known on this device. Pick it to load the media.</p>
            <button
              className="btn primary small"
              disabled={busy}
              onClick={() => onConnect(false)}
              {...tip('Pick the folder on your disk that contains this project')}
            >
              Choose folder…
            </button>
          </div>
        </div>
      );
    case 'needs-permission':
      return (
        <div className="media-folder-callout warn">
          <FolderSync size={18} />
          <div>
            <strong>Reconnect “{state.label}”</strong>
            <p>Your browser needs permission again to read this project’s folder.</p>
            <div className="row">
              <button
                className="btn primary small"
                disabled={busy}
                onClick={() => onConnect(false)}
                {...tip(`Allow access to “${state.label}” for this session (Chrome can remember it)`)}
              >
                Reconnect
              </button>
              <button
                className="btn small ghost"
                disabled={busy}
                onClick={() => onConnect(true)}
                {...tip('Pick a different folder, e.g. if you moved or renamed it')}
              >
                Locate…
              </button>
            </div>
          </div>
        </div>
      );
    case 'unsupported':
      return (
        <div className="media-folder-callout error">
          <AlertTriangle size={18} />
          <div>
            <strong>Storage unavailable</strong>
            <p>{state.reason}</p>
          </div>
        </div>
      );
    case 'ready':
      if (!isImport) return null; // read-only providers: nothing to manage
      return (
        <div className="media-folder-ready">
          <HardDrive size={13} />
          <span
            className="media-folder-name"
            {...tip(`Project folder: ${state.label} — media is read from and imported into it`)}
          >
            {state.label}
          </span>
          {offline > 0 && (
            <span className="media-folder-offline" {...tip('Files that could not be found in this folder')}>
              {offline} offline
            </span>
          )}
        </div>
      );
  }
}
